/**
 * Membership Year (Anno Sociale) Utilities
 *
 * Nel sistema UISP l'anno sociale va dal 1° settembre al 31 agosto.
 * La stagione è identificata dall'anno di INIZIO (convenzione UISP API).
 *   Esempio: stagione 1° set 2025 → 31 ago 2026 è chiamata "2025".
 *
 * Regola:
 *   - mesi 1-8  (gen-ago): membership_year = anno calendaristico - 1
 *   - mesi 9-12 (set-dic): membership_year = anno calendaristico corrente
 */

const SOCIAL_YEAR_START_MONTH = 9;

/**
 * Restituisce l'anno sociale (anno di INIZIO) per la data fornita.
 * Se la data cade da settembre a dicembre, l'anno sociale è quello corrente.
 * Se la data cade da gennaio ad agosto, l'anno sociale è quello precedente
 * (perché la stagione è iniziata a settembre dell'anno precedente).
 *
 * @param {Date|string|number} [dateInput] - Data di riferimento (default: now)
 * @returns {number} - Anno sociale (es. 2025 per stagione 2025/2026)
 *
 * @example
 *   getMembershipYear(new Date('2025-08-31')) // 2025 (stagione 2025/2026)
 *   getMembershipYear(new Date('2025-09-01')) // 2025 (stagione 2025/2026)
 *   getMembershipYear(new Date('2026-01-15')) // 2025 (stagione 2025/2026)
 *   getMembershipYear(new Date('2026-09-10')) // 2026 (stagione 2026/2027)
 */
export function getMembershipYear(dateInput) {
  const date = dateInput === null || dateInput === undefined ? new Date() : new Date(dateInput);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return month >= SOCIAL_YEAR_START_MONTH ? year : year - 1;
}

/**
 * Restituisce l'anno sociale corrente (basato sulla data attuale).
 * @returns {number}
 */
export function getCurrentMembershipYear() {
  return getMembershipYear(new Date());
}

/**
 * Restituisce l'anno sociale precedente a quello corrente.
 * @returns {number}
 */
export function getPreviousMembershipYear() {
  return getCurrentMembershipYear() - 1;
}

/**
 * Restituisce l'elenco degli anni sociali disponibili come array ordinato
 * descending. Utile per popolare dinamicamente i selettori anno nel frontend.
 *
 * @param {number} [count] - Numero di anni passati da includere (default 5)
 * @param {number} [startYear] - Primo anno storico da considerare (default: 2020)
 * @returns {number[]}
 *
 * @example
 *   getAvailableMembershipYears() // [2027, 2026, 2025, 2024, 2023, 2022]
 */
export function getAvailableMembershipYears(count = 5, startYear = 2020) {
  const current = getCurrentMembershipYear();
  const years = [];
  for (let y = current; y >= startYear && years.length <= count; y--) {
    years.push(y);
  }
  return years;
}
